import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { phoneNumberFormat } from '@willow/shared-iso';
import {
  Button,
  ControlledInput,
  ControlledPasswordInput,
  useZodHookForm,
  VerticalSpacing,
  z,
} from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { PORTAL_LOGIN_EMAIL_QUERY_PARAM, PortalLoginConfigEnabled } from '@willow/types-iso';

import { authEmailSchema } from '../../auth/authFormSchema';
import { AuthLayout } from '../../auth/AuthLayout';
import { FORGOT_PASSWORD_PATH, safeReturnTo } from '../../auth/authRoutes';
import { hardNavigate } from '../../auth/hardNavigate';
import { signIn } from '../../auth/portalAuthApi';
import { Alert } from '../../components/alert/Alert';

const FormShape = z.object({
  email: authEmailSchema,
  // No minimum length: imported Auth0 credentials are 12 characters and must keep signing in (§10.2).
  password: z.string().min(1, { error: 'Enter your password' }),
});

type SubmitError = 'CREDENTIALS' | 'NETWORK';

const SUPPORT_HINT_AFTER_FAILURES = 3;

interface Props {
  config: PortalLoginConfigEnabled;
}

export const LoginPage = ({ config }: Props) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const [submitError, setSubmitError] = useState<SubmitError>();
  const [failureCount, setFailureCount] = useState(0);

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    setValue,
    watch,
  } = useZodHookForm(FormShape, {
    defaultValues: { email: params.get(PORTAL_LOGIN_EMAIL_QUERY_PARAM)?.trim() ?? '', password: '' },
  });

  const onSubmit = handleSubmit(async (data) => {
    const result = await signIn({ email: data.email, password: data.password });
    if (result.ok) {
      hardNavigate(safeReturnTo(params.get('returnTo')));
      return;
    }
    setValue('password', '');
    setFailureCount((count) => count + 1);
    // Throttled and locked-out answers are indistinguishable from a wrong password by design
    // (§10.6, §10.7), so RATE_LIMITED lands in the same branch.
    setSubmitError(result.code === 'NETWORK' || (result.status ?? 500) >= 500 ? 'NETWORK' : 'CREDENTIALS');
  });

  const typedEmail = watch('email').trim();
  const forgotPasswordTo = typedEmail
    ? `${FORGOT_PASSWORD_PATH}?email=${encodeURIComponent(typedEmail)}`
    : FORGOT_PASSWORD_PATH;

  // The screens carry no permanent support block, so the lender's contact details surface here
  // instead, at the point a borrower is actually stuck. The trigger stays the failure count
  // rather than the response code, because a throttled or locked-out answer has to stay
  // indistinguishable from a wrong password (§10.6, §10.7).
  const { support } = config;
  let credentialsSubtitle: ReactNode = 'Check both and try again, or reset your password.';
  if (failureCount >= SUPPORT_HINT_AFTER_FAILURES) {
    credentialsSubtitle = support ? (
      <>
        Still having trouble? Reset your password, or contact{' '}
        <a href={`tel:${support.phone}`}>{phoneNumberFormat(support.phone)}</a> or{' '}
        <a href={`mailto:${support.email}`}>{support.email}</a> for help.
      </>
    ) : (
      'Reset your password or contact your lender for help.'
    );
  }

  return (
    <AuthLayout
      portal={config.portal}
      documentTitle="Log in"
      title="Welcome to your loan portal"
      description="Log in using the email address associated with your loan. Use the reset password link to set or create a new password."
      faq="login"
    >
      <div className="auth-layout__aligned" role="alert" aria-live="assertive">
        {submitError === 'CREDENTIALS' && (
          <Alert
            level="ERROR"
            title="That email and password do not match our records."
            subtitle={credentialsSubtitle}
          />
        )}
        {submitError === 'NETWORK' && (
          <Alert
            level="ERROR"
            title="We could not log you in right now."
            subtitle="Please check your connection and try again in a moment."
          />
        )}
      </div>

      <Form onSubmit={onSubmit}>
        <VerticalSpacing spacing={2}>
          <Form.Group controlId="email">
            <ControlledInput
              control={control}
              fieldName="email"
              label="Email"
              inputType="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
            />
          </Form.Group>

          <Form.Group controlId="password">
            <ControlledPasswordInput
              control={control}
              fieldName="password"
              label="Password"
              autoComplete="current-password"
              iconOnlyToggle
            />
            <Link className="auth-layout__link" to={forgotPasswordTo}>
              Reset Password
            </Link>
          </Form.Group>

          <Button
            type="submit"
            size="lg"
            className="w-100"
            innerClassName="justify-content-center"
            loading={isSubmitting}
          >
            Log in
          </Button>
        </VerticalSpacing>
      </Form>
    </AuthLayout>
  );
};
